import * as React from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CssBaseline from '@mui/material/CssBaseline';
import AppTheme from '../shared-theme/AppTheme';
import { useAuth } from '../context/AuthContext';

export default function LoginPage(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, error: authError } = useAuth();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const from = location.state?.from?.pathname || '/app';

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error || 'Failed to log in');
    }

    setLoading(false);
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card
          variant="outlined"
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Stack spacing={3}>
            {/* Logo/Brand */}
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src="/icon.svg"
                alt=""
                sx={{
                  width: 60,
                  height: 60,
                  objectFit: 'contain',
                  mb: 1,
                }}
              />
              <Box
                component="img"
                src="/logo.svg"
                alt="myGrow Collective"
                sx={{
                  width: 200,
                  height: 'auto',
                  objectFit: 'contain',
                  mb: 1,
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Sign in to your account
              </Typography>
            </Box>

            {/* Error Alert */}
            {(error || authError) && (
              <Alert severity="error">{error || authError}</Alert>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Email *
                  </Typography>
                  <TextField
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    autoComplete="email"
                    autoFocus
                    placeholder="Enter your email"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Password *
                  </Typography>
                  <TextField
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                  />
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </Stack>
            </form>

            <Divider>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                or
              </Typography>
            </Divider>

            {/* Sign Up Link */}
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Don't have an account?{' '}
              <Link component={RouterLink} to="/signup" underline="hover">
                Sign up
              </Link>
            </Typography>

            {/* Demo Link */}
            <Button
              component={RouterLink}
              to="/demo"
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Try Demo Mode
            </Button>
          </Stack>
        </Card>
      </Box>
    </AppTheme>
  );
}
